/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { ActivatedRoute, ActivatedRouteSnapshot, CanActivate } from '@angular/router';
import { Injectable } from '@angular/core';
import { shareReplay, tap } from 'rxjs/operators';
import { firstValueFrom, Observable, of } from 'rxjs';
import {
    AbstractService,
    CommonService,
    Config,
    SessionController,
} from '@increff/commons';
import { Rest } from './rest.service';
import { DOMAIN_DETAILS } from '../interfaces/authentication.interface';
import { HttpParams } from '@angular/common/http';

@Injectable({
    providedIn: 'root',
})
export class AuthNGuardService implements CanActivate {
    private _currentUser$: Observable<any>;
    private _currentUser: any = {};
    private isSuperAdminUserFlow = false;
    private _sessionData$: Observable<any>;
    private sessionDataLoading: boolean;

    constructor(
        protected commonService: CommonService,
        protected sessionController: SessionController,
        protected abstractService: AbstractService,
        private urls: Rest,
        private config: Config,
        private activatedRoute: ActivatedRoute
    ) {
        const usrVal = localStorage.getItem(
            this.commonService.cipher('currentUserObj')
        );
        if (usrVal)
            try {
                this.currentUser = JSON.parse(
                    this.commonService.decipher(usrVal)
                );
            } catch (e) {
                this.currentUser = undefined;
            }
    }

    get currentUser$(): Observable<any> {
        return this._currentUser$;
    }

    set currentUser$(value: any) {
        this._currentUser$ = value;
    }

    get currentUser(): any {
        return this._currentUser;
    }

    set currentUser(value: any) {
        this._currentUser = value;
    }

    getSessionData$(route: ActivatedRouteSnapshot, resetValue: boolean = false) {
        if(resetValue) this._sessionData$ = undefined;

        if (!this._sessionData$ && !this.sessionDataLoading) {
            this.sessionDataLoading = true;
            this._sessionData$ = this.validateSession(route).pipe(
                tap(() => (this.sessionDataLoading = false)),
                shareReplay(1)
            );
        }
        return this._sessionData$;
    }

    getDomainDetails(domainId?: number, type: 'admin' | 'domain' = 'admin'): Observable<DOMAIN_DETAILS> {
        const params = new HttpParams()?.set('domainId', domainId);
        return this.abstractService?.exec(
            type === 'admin' ? this.urls?.adminDomainDetailsUrl : this.urls?.domainDetailsUrl,
            'GET',
            undefined,
            type === 'admin' ? { params} : {}
        );
    }

    async canActivate(route: ActivatedRouteSnapshot): Promise<any> {
        const encryptedVal = localStorage.getItem(
            this.commonService.cipher('lastCheckTime')
        );
        const lastCheckTime = encryptedVal
            ? this.commonService.decipher(encryptedVal)
            : undefined;
        const now = Date.now();

        const diff = lastCheckTime ? now - Number(lastCheckTime) : now;
        if (diff < 45 * 1000 && this.currentUser) return true;

        this.currentUser = Object?.assign({}, {});
        return await this.setCurrentUserObs(route, true);
    }

    async setCurrentUserObs(route?: ActivatedRouteSnapshot, resetValue: boolean = false) {
        const now = Date.now();
        try {
            const res = await this.checkSession(route ?? this.activatedRoute?.snapshot, resetValue);
            if (res?.code != 'AUTH_ERROR') {
                if (res?.principal?.fullname || res?.principal?.fullName) {
                    localStorage.setItem(
                        this.commonService.cipher('lastCheckTime'),
                        this.commonService.cipher(`${now}`)
                    );
                    const tempObj = res?.principal;
                    tempObj['isSuperAdminUser'] = tempObj?.roles?.includes(
                        this.config?.startupData?.adminFlowRole
                    );
                    this.currentUser = Object?.assign({}, { ...tempObj });
                    this._currentUser$ = of(this.currentUser);
                    localStorage.setItem(
                        this.commonService.cipher('currentUserObj'),
                        this.commonService.cipher(
                            JSON.stringify(this.currentUser)
                        )
                    );
                    return true;
                }
                return false;
            }
            window.location.href = this.urls?.loginUrl;
            return false;
        } catch (error) {
            window.location.href = this.urls?.loginUrl;
            return false;
        }
    }

    async checkSession(route: ActivatedRouteSnapshot, resetValue: boolean = false): Promise<any> {
        try {
            const response = await firstValueFrom(this.getSessionData$(route, resetValue));
            return response;
        } catch (err) {
            err['_body'] = 'Response body is not a proper JSON';
            return Promise.reject(err);
        }
    }

    private validateSession(route: ActivatedRouteSnapshot) {
        this.isSuperAdminUserFlow =
            route?.data?.flow === 'admin' ? true : false;
        return this.abstractService.exec(
            this.isSuperAdminUserFlow
                ? this.urls.adminAuthenticationUrl
                : this.urls.domainUserAuthenticationUrl,
            'GET',
            undefined,
            {
                hideToast: true,
                hideSnackbar: true,
                ...this.config?.startupData?.extraHttpOptions,
            },
            1
        );
    }
}
